import type { TransportState } from '../audio/Scheduler';
import type { ArpPatternName } from '../audio/Arpeggiator';

export const EDIT_MODES = {
  DEFAULT: 'EDIT_MODE.DEFAULT',
  ADSR_A: 'EDIT_MODE.ADSR_A',
  ADSR_D: 'EDIT_MODE.ADSR_D',
  ADSR_S: 'EDIT_MODE.ADSR_S',
  ADSR_R: 'EDIT_MODE.ADSR_R',
  ARP_SELECT: 'EDIT_MODE.ARP_SELECT',
  LOOP_EDIT: 'EDIT_MODE.LOOP_EDIT',
} as const;

export type EditMode = (typeof EDIT_MODES)[keyof typeof EDIT_MODES];

export const RECORD_STATES = {
  COUNTING_IN: 'COUNTING_IN',
  RECORDING: 'RECORDING',
  OVERWRITING: 'OVERWRITING',
  STOPPED: 'STOPPED',
} as const;

export type RecordState = (typeof RECORD_STATES)[keyof typeof RECORD_STATES];

export const SAMPLE_MODES = { RECORD: 'RECORD', OVERWRITE: 'OVERWRITE' } as const;
export type SampleMode = (typeof SAMPLE_MODES)[keyof typeof SAMPLE_MODES];

export const SAMPLE_SELECTIONS: readonly string[] = [
  './assets/samples/01_Piano.mp3',
  './assets/samples/02_Strings.mp3',
  './assets/samples/03_Pizzicato.mp3',
  './assets/samples/04_BassGuitar.mp3',
  './assets/samples/05_Glow.mp3',
  // Slot 6 is the user's own recording — it has no file to load.
  '',
  './assets/samples/06_ AirdockMoment.mp3',
  './assets/samples/07_Synthwave.mp3',
  './assets/samples/08_ChurchOrgan.mp3',
  './assets/samples/09_FMko.mp3',
  './assets/samples/10_FMBell.mp3',
  './assets/samples/11_808.mp3',
];

/** Slot index that holds the live sampled buffer rather than a file. */
export const USER_SAMPLE_SLOT = 5;

export interface PanelState {
  VIBRATO: boolean;
  EDIT_MODE: EditMode;
  SELECTION_MODE: EditMode;
  ADSR: readonly [number, number, number, number];
  LOOP: boolean;
  LOOP_END: number;
  PINGPONG: boolean;
  REVERSE: boolean;
  ECHO: boolean;
  FUZZ: boolean;
  FILTER: boolean;
  FILTER_CUTOFF: number;
  REVERB: boolean;
  REVERB_SIZE: number;
  SAMPLE_MODE: SampleMode;
  SAMPLE_SELECTION: string;
  SAMPLE_SELECTION_VALUE: number;
  ARP_ENABLED: boolean;
  ARP_SELECTION: ArpPatternName;
  ARP_SELECTION_VALUE: number;
  BPM: number;
  BPM_MS: number;
  RECORD_STATE: RecordState;
  /** `performance.now()` when the current count-in or take began. */
  RECORD_START: number;
  MUSIC_RECORDER_STATE: TransportState;
  MUSIC_RECORDER_POS: number;
  DEMO_STATE: TransportState;
}

/** Every command name that a panel control can dispatch. */
export type PanelCommand =
  | 'Button_AllCancel'
  | 'Button_Arp_Stop'
  | 'Button_Demo_StartStop'
  | 'Button_MP_PlayStop'
  | 'Button_MP_Record'
  | 'Button_Tempo_Down'
  | 'Button_Tempo_Up'
  | 'Button_DataEntry_Down'
  | 'Button_DataEntry_Up'
  | 'Button_Attack'
  | 'Button_Decay'
  | 'Button_Sustain'
  | 'Button_Release'
  | 'Button_Loop'
  | 'Button_PingPong'
  | 'Button_Reverse'
  | 'Button_Echo'
  | 'Button_Fuzz'
  | 'Button_Filter'
  | 'Button_Reverb'
  | 'Button_Vibrato'
  | 'Button_Sample'
  | 'Button_Overwrite'
  | 'Button_ModeSelect_ArpSelect'
  | `Button_Select_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12}`;

/** Continuous controls, addressed by their mesh name. */
export type PanelFader = 'Fader_ReverbSize' | 'Fader_FilterCutoff';

export const FADER_TO_KEY: Record<PanelFader, 'REVERB_SIZE' | 'FILTER_CUTOFF'> = {
  Fader_ReverbSize: 'REVERB_SIZE',
  Fader_FilterCutoff: 'FILTER_CUTOFF',
};
